import React, { useCallback, useEffect, useMemo, useState } from "react";
import { filterPackages } from "../../../../scripts/commons/common";
import {
  type AdditionalPackagesContext,
  PackageKind,
  getNewCarRentServicePackageValues,
  getPackageKind,
  getPackageKindFromValue,
  getPackageValueForKind,
  resolveAdditionalPackagesContext,
  usesUsedCalcTooltips
} from "./packageEnumUtils";
import AdditionalPackageTile from "../../../parts/AdditionalPackageTile";
import type {
  CalculatorData,
  LeasingAdditions,
  RentAdditionServiceConfig,
  RentAdditions
} from "../types/calculatorData";
import { getActiveRentSelectionFromTree } from "./getActiveCalculatorSelection";
import { getSelectedPricesFromCalculatorData } from "./getSelectedCalculatorPrice";

export interface PackageDependency {
  packageStateName: string;
  valueRequired: boolean;
  action: string;
}

export interface AdditionalServiceItem {
  IconName: string;
  Name: string;
  Tooltip?: string;
  EkstraTooltip?: string;
  Indent?: boolean;
  CantBeDeactivated?: boolean;
  SetSelfAsTrue?: boolean;
  Disabled?: boolean;
  Enabled?: boolean;
  Visible?: boolean;
  Value: number;
  HiddenPrice?: boolean;
  Price?: number | null;
  Dependency?: PackageDependency[] | null;
  Visibility?: { packageStateName: string; valueRequired: boolean }[] | null;
}

export interface PackageStateEntry {
  isChecked: boolean;
  Value: number;
  Price?: number | null;
  isVisible?: boolean;
}

export type PackageState = Record<string, PackageStateEntry>;

const SERVICE_PACKAGE_VALUES = getNewCarRentServicePackageValues();

export const getPackageStateKey = (name: string): string => name.split(" ").join("_");

export type { AdditionalPackagesContext };
export { resolveAdditionalPackagesContext };

function getRentTyresPrice(
  rentAdditions: RentAdditions,
  selectedPeriod: number | null | undefined,
  isAfterLeaseRent: boolean
): number | null {
  if (isAfterLeaseRent) {
    return typeof rentAdditions.Tires === "number" ? rentAdditions.Tires : null;
  }

  if (Array.isArray(rentAdditions.Tires) && selectedPeriod != null) {
    return rentAdditions.Tires.find((item) => item.Period === selectedPeriod)?.Value ?? null;
  }

  return null;
}

function getLeasingTyresPrice(additions: RentAdditions | LeasingAdditions): number | null {
  if ("Tires" in additions && typeof additions.Tires === "number") {
    return additions.Tires;
  }

  return null;
}

function getPetrolCardPrice(
  additions: RentAdditions | LeasingAdditions,
  fallbackAdditions?: LeasingAdditions | null
): number | null {
  if ("PetrolCard" in additions && additions.PetrolCard != null && additions.PetrolCard > 0) {
    return additions.PetrolCard;
  }

  if (fallbackAdditions?.PetrolCard != null && fallbackAdditions.PetrolCard > 0) {
    return fallbackAdditions.PetrolCard;
  }

  return "PetrolCard" in additions ? additions.PetrolCard ?? null : null;
}

function getTechnicalServicePrice(
  additions: RentAdditions | LeasingAdditions,
  fallbackAdditions?: LeasingAdditions | null
): number | null {
  if ("TechnicalService" in additions && additions.TechnicalService != null && additions.TechnicalService > 0) {
    return additions.TechnicalService;
  }

  if (fallbackAdditions?.TechnicalService != null && fallbackAdditions.TechnicalService > 0) {
    return fallbackAdditions.TechnicalService;
  }

  if ("TechnicalService" in additions) {
    return additions.TechnicalService ?? null;
  }

  return null;
}

const TOGGLE_SELF_WITH_DEP = "TOGGLE_SELF_WITH_DEP";

type PackageBuildOptions = {
  literals: Record<string, string | undefined> | null | undefined;
  isElectric: boolean;
  additions: RentAdditions | LeasingAdditions;
  priceFallbackAdditions?: LeasingAdditions | null;
};

type PackageDefinition = {
  icon: string;
  name: string;
  tooltip: (context: AdditionalPackagesContext, literals: PackageBuildOptions["literals"]) => string | undefined;
  dependency?: PackageDependency[];
  getPrice?: (additions: RentAdditions | LeasingAdditions, fallback?: LeasingAdditions | null) => number | null;
  shouldInclude?: (
    item: RentAdditionServiceConfig,
    context: AdditionalPackagesContext,
    options: PackageBuildOptions
  ) => boolean;
};

const PACKAGE_DEFINITIONS: Partial<Record<PackageKind, PackageDefinition>> = {
  [PackageKind.Insurance]: {
    icon: "ShieldTick",
    name: "ubezpieczenie",
    tooltip: (context, literals) =>
      literals?.[usesUsedCalcTooltips(context) ? "calc_used_tooltip_insurance" : "calc_new_tooltip_insurance"]
  },
  [PackageKind.Assistance]: {
    icon: "CarPhoneHandle",
    name: "assistance",
    tooltip: (context, literals) =>
      literals?.[usesUsedCalcTooltips(context) ? "calc_used_tooltip_assistance" : "calc_new_tooltip_assistance"],
    getPrice: (additions) => additions.Assistance
  },
  [PackageKind.PetrolCard]: {
    icon: "CreditCard",
    name: "karta paliwowa",
    tooltip: (context, literals) =>
      literals?.[usesUsedCalcTooltips(context) ? "calc_used_tooltip_fuel_card" : "calc_new_tooltip_fuel_card"],
    getPrice: (additions, fallback) => getPetrolCardPrice(additions, fallback)
  },
  [PackageKind.Tyres]: {
    icon: "Tire",
    name: "pakiet Opony",
    tooltip: (_context, literals) => literals?.calc_new_tooltip_tire_package,
    getPrice: (additions) => getLeasingTyresPrice(additions)
  },
  [PackageKind.TechnicalService]: {
    icon: "Wrench",
    name: "koordynacja serwisu",
    tooltip: (_context, literals) => literals?.calc_used_tooltip_service_support,
    getPrice: (additions, fallback) => getTechnicalServicePrice(additions, fallback)
  },
  [PackageKind.ManagementBasic]: {
    icon: "Wrench",
    name: "pakiet serwisowy Basic",
    tooltip: (_context, literals) => literals?.calc_new_tooltip_service_support_basic,
    dependency: [{ packageStateName: "pakiet_serwisowy_Comfort", valueRequired: true, action: TOGGLE_SELF_WITH_DEP }],
    getPrice: (additions) => getTechnicalServicePrice(additions)
  },
  [PackageKind.ManagementComfort]: {
    icon: "Wrench",
    name: "pakiet serwisowy Comfort",
    tooltip: (_context, literals) => literals?.calc_new_tooltip_service_support_comfort,
    dependency: [{ packageStateName: "pakiet_serwisowy_Basic", valueRequired: true, action: TOGGLE_SELF_WITH_DEP }],
    getPrice: (additions) => getTechnicalServicePrice(additions)
  },
  [PackageKind.ManagementPrestigeNonAso]: {
    icon: "Wrench",
    name: "pakiet serwisowy Prestige",
    tooltip: (_context, literals) => literals?.calc_new_tooltip_service_support_prestige,
    dependency: [{ packageStateName: "pakiet_serwisowy_Prestige", valueRequired: true, action: TOGGLE_SELF_WITH_DEP }]
  },
  [PackageKind.ManagementPrestige]: {
    icon: "Wrench",
    name: "pakiet serwisowy Prestige",
    tooltip: (_context, literals) => literals?.calc_new_tooltip_service_support_prestige,
    dependency: [
      { packageStateName: "pakiet_serwisowy_Prestige_nonASO", valueRequired: true, action: TOGGLE_SELF_WITH_DEP }
    ]
  }
};

function buildServiceItemFromConfig(
  context: AdditionalPackagesContext,
  kind: PackageKind,
  item: RentAdditionServiceConfig,
  options: PackageBuildOptions
): AdditionalServiceItem | null {
  const definition = PACKAGE_DEFINITIONS[kind];
  if (!definition || !item.Visible) {
    return null;
  }

  if (definition.shouldInclude && !definition.shouldInclude(item, context, options)) {
    return null;
  }

  const serviceItem: AdditionalServiceItem = {
    IconName: definition.icon,
    Name: definition.name,
    Tooltip: definition.tooltip(context, options.literals),
    Indent: false,
    CantBeDeactivated: false,
    SetSelfAsTrue: item.DefaultSelected,
    Disabled: !item.Enabled,
    Visible: item.Visible,
    Value: getPackageValueForKind(kind),
    HiddenPrice: item.HiddenPrice
  };

  if (definition.dependency) {
    serviceItem.Dependency = definition.dependency;
  }

  if (definition.getPrice) {
    serviceItem.Price = definition.getPrice(options.additions, options.priceFallbackAdditions);
  }

  return serviceItem;
}

function needsUsedCalcStyle(pageType: string, activeTab: number): boolean {
  return pageType === "afterLease" || (pageType === "new" && activeTab === 2);
}

function applyUsedCalcStyle(
  items: AdditionalServiceItem[],
  literals: Record<string, string | undefined> | null | undefined
): AdditionalServiceItem[] {
  return items.map((item) => {
    const kind = getPackageKindFromValue(item.Value);
    if (!kind) {
      return item;
    }

    const styled = { ...item, Indent: false, Visibility: null };

    switch (kind) {
      case PackageKind.Tyres:
        return {
          ...styled,
          Name: styled.Name.toLowerCase(),
          Tooltip: literals?.calc_new_tooltip_tire_package
        };
      case PackageKind.PetrolCard:
        return {
          ...styled,
          Name: styled.Name.toLowerCase(),
          Tooltip: literals?.calc_used_tooltip_fuel_card
        };
      case PackageKind.TechnicalService:
        return {
          ...styled,
          Name: "koordynacja serwisu",
          Tooltip: literals?.calc_used_tooltip_service_support
        };
      case PackageKind.Assistance:
        return {
          ...styled,
          Name: styled.Name.toLowerCase(),
          Tooltip: literals?.calc_used_tooltip_assistance
        };
      case PackageKind.Insurance:
        return {
          ...styled,
          Name: styled.Name.toLowerCase(),
          Tooltip: literals?.calc_used_tooltip_insurance
        };
      default:
        return styled;
    }
  });
}

export function buildAdditionalServiceItemsFromAdditions(
  additions: RentAdditions | LeasingAdditions,
  literals: Record<string, string | undefined> | null | undefined,
  isElectric: boolean,
  activeTab: number,
  pageType = "new",
  priceFallbackAdditions?: LeasingAdditions | null
): AdditionalServiceItem[] {
  const context = resolveAdditionalPackagesContext(pageType, activeTab);
  const buildOptions: PackageBuildOptions = {
    literals,
    isElectric,
    additions,
    priceFallbackAdditions
  };

  const packages = filterPackages(additions.Configuration).flatMap((item: RentAdditionServiceConfig) => {
    const kind = getPackageKind(context, item.ServiceId);
    if (!kind) {
      return [];
    }

    const serviceItem = buildServiceItemFromConfig(context, kind, item, buildOptions);
    return serviceItem ? [serviceItem] : [];
  });

  if (needsUsedCalcStyle(pageType, activeTab)) {
    return applyUsedCalcStyle(packages, literals);
  }

  return packages;
}

export function mergeAdditionalServiceItems(
  ...itemGroups: AdditionalServiceItem[][]
): AdditionalServiceItem[] {
  const byValue = new Map<number, AdditionalServiceItem>();
  itemGroups.flat().forEach((item) => byValue.set(item.Value, item));
  return Array.from(byValue.values());
}

export function createPackageStateFromItems(
  additionalServices: AdditionalServiceItem[],
  queryObjectWithState?: Record<string, string> | null
): PackageState {
  const stateObj: PackageState = {};

  for (const pkg of additionalServices) {
    const key = getPackageStateKey(pkg.Name);
    const entry: PackageStateEntry = {
      isChecked: !!pkg.SetSelfAsTrue,
      Value: pkg.Value
    };

    entry.Price = Array.isArray(pkg.Price) ? (pkg.Price as unknown as { Value: number }[])[0]?.Value : pkg.Price ?? 0;

    entry.isVisible = !(pkg.Visibility && pkg.Visibility.length > 0);

    if (queryObjectWithState?.[`Package${pkg.Value}`]) {
      entry.isChecked = queryObjectWithState[`Package${pkg.Value}`] === "true";
      if (entry.isChecked) {
        entry.isVisible = true;
      }
    }

    stateObj[key] = entry;
  }

  return stateObj;
}

/** Ensures every visible item has a state entry (fixes first render & tab-specific name keys). */
export function ensurePackageStateForItems(
  state: PackageState,
  items: AdditionalServiceItem[]
): PackageState {
  const nextState = { ...state };

  for (const item of items) {
    const key = getPackageStateKey(item.Name);
    const price = Array.isArray(item.Price)
      ? (item.Price as unknown as { Value: number }[])[0]?.Value
      : item.Price ?? 0;

    if (!nextState[key]) {
      nextState[key] = {
        isChecked: !!item.SetSelfAsTrue,
        Value: item.Value,
        Price: price,
        isVisible: !(item.Visibility && item.Visibility.length > 0)
      };
    } else {
      nextState[key] = {
        ...nextState[key],
        Value: item.Value,
        Price: price
      };
    }
  }

  return nextState;
}

/** Keeps only package state entries for the currently visible tab items. */
export function filterPackageStateForItems(
  state: PackageState,
  items: AdditionalServiceItem[]
): PackageState {
  const filtered: PackageState = {};

  for (const item of items) {
    const key = getPackageStateKey(item.Name);
    if (state[key]) {
      filtered[key] = state[key];
    }
  }

  return filtered;
}

function getRentManagementPrice(
  rentAdditions: RentAdditions,
  managementKey:
    | "Rent_ManagementBasic"
    | "Rent_ManagementComfort"
    | "Rent_ManagementPrestige"
    | "Rent_ManagementPrestigeAso",
  period: number,
  mileageTotal: number
): number | null {
  const periodEntry = rentAdditions[managementKey]?.find((item) => item.Period === period);
  return periodEntry?.Mileages?.find((item) => item.MileageTotal === mileageTotal)?.Value ?? null;
}

export function applyRentPackagePrices(
  packages: AdditionalServiceItem[],
  calculatorData: CalculatorData,
  insurancePrice: number | null | undefined,
  insuranceAdditionalText?: string | null,
  pageType = "new"
): AdditionalServiceItem[] {
  const selection = getActiveRentSelectionFromTree(calculatorData.RentPeriods ?? []);
  const rentAdditions = calculatorData.RentAdditions;
  const selectedPeriod = selection?.period.Value;
  const selectedMileageTotal = selection?.mileage.Value;
  const isAfterLeaseRent = pageType === "afterLease";

  if (!rentAdditions) {
    return packages;
  }

  return packages.map((pkg) => {
    const updated = { ...pkg };
    const kind = getPackageKindFromValue(pkg.Value);

    switch (kind) {
      case PackageKind.Insurance:
        updated.Price = insurancePrice ?? null;
        if (insuranceAdditionalText) {
          updated.EkstraTooltip = ` ${insuranceAdditionalText}`;
        }
        break;
      case PackageKind.Assistance:
        updated.Price = rentAdditions.Assistance;
        break;
      case PackageKind.ManagementBasic:
      case PackageKind.TechnicalService:
        updated.Price = isAfterLeaseRent
          ? getTechnicalServicePrice(rentAdditions, calculatorData.LeasingAdditions)
          : selectedPeriod != null && selectedMileageTotal != null
            ? getRentManagementPrice(rentAdditions, "Rent_ManagementBasic", selectedPeriod, selectedMileageTotal)
            : null;
        break;
      case PackageKind.ManagementComfort:
        updated.Price =
          selectedPeriod != null && selectedMileageTotal != null
            ? getRentManagementPrice(rentAdditions, "Rent_ManagementComfort", selectedPeriod, selectedMileageTotal)
            : null;
        break;
      case PackageKind.ManagementPrestigeNonAso:
        updated.Price =
          selectedPeriod != null && selectedMileageTotal != null
            ? getRentManagementPrice(rentAdditions, "Rent_ManagementPrestige", selectedPeriod, selectedMileageTotal)
            : null;
        break;
      case PackageKind.ManagementPrestige:
        updated.Price =
          selectedPeriod != null && selectedMileageTotal != null
            ? getRentManagementPrice(rentAdditions, "Rent_ManagementPrestigeAso", selectedPeriod, selectedMileageTotal)
            : null;
        break;
      case PackageKind.Tyres:
        updated.Price = getRentTyresPrice(rentAdditions, selectedPeriod, isAfterLeaseRent);
        break;
      case PackageKind.PetrolCard:
        updated.Price = getPetrolCardPrice(rentAdditions, calculatorData.LeasingAdditions);
        break;
      default:
        break;
    }

    return updated;
  });
}

export function applyLeasingPackagePrices(
  packages: AdditionalServiceItem[],
  calculatorData: CalculatorData,
  insurancePrice: number | null | undefined,
  insuranceAdditionalText?: string | null
): AdditionalServiceItem[] {
  const leasingAdditions = calculatorData.LeasingAdditions;

  return packages.map((pkg) => {
    const updated = { ...pkg };
    const kind = getPackageKindFromValue(pkg.Value);

    switch (kind) {
      case PackageKind.Insurance:
        updated.Price = insurancePrice ?? null;
        if (insuranceAdditionalText) {
          updated.EkstraTooltip = ` ${insuranceAdditionalText}`;
        }
        break;
      case PackageKind.Assistance:
        updated.Price = leasingAdditions.Assistance;
        break;
      case PackageKind.TechnicalService:
        updated.Price = leasingAdditions.TechnicalService ?? null;
        break;
      case PackageKind.Tyres:
        updated.Price = leasingAdditions.Tires ?? null;
        break;
      case PackageKind.PetrolCard:
        updated.Price = leasingAdditions.PetrolCard ?? null;
        break;
      default:
        break;
    }

    return updated;
  });
}

function toggleNewCarRentServicePackage(
  pkgState: PackageState,
  pkgName: string,
  pkg: AdditionalServiceItem,
  rentItems: AdditionalServiceItem[]
): PackageState {
  const nextState = { ...pkgState };
  const wasChecked = nextState[pkgName].isChecked;

  if (wasChecked) {
    const servicePackages = rentItems
      .filter((item) => SERVICE_PACKAGE_VALUES.includes(item.Value))
      .sort((a, b) => SERVICE_PACKAGE_VALUES.indexOf(b.Value) - SERVICE_PACKAGE_VALUES.indexOf(a.Value));

    const currentIndex = servicePackages.findIndex((item) => item.Name === pkg.Name);
    const nextIndex = (currentIndex + 1) % servicePackages.length;
    const nextPackage = servicePackages[nextIndex];

    servicePackages.forEach((item) => {
      nextState[getPackageStateKey(item.Name)].isChecked = false;
    });

    if (nextPackage) {
      nextState[getPackageStateKey(nextPackage.Name)].isChecked = true;
    }
  } else {
    Object.keys(nextState).forEach((itemKey) => {
      if (
        rentItems.some(
          (item) => getPackageStateKey(item.Name) === itemKey && SERVICE_PACKAGE_VALUES.includes(item.Value)
        )
      ) {
        nextState[itemKey].isChecked = false;
      }
    });
    nextState[pkgName].isChecked = true;
  }

  return nextState;
}

function toggleNewCarRentDependencyPackage(
  pkgState: PackageState,
  pkg: AdditionalServiceItem,
  rentItems: AdditionalServiceItem[]
): PackageState {
  const nextState = { ...pkgState };

  pkg.Dependency?.forEach((dep) => {
    if (dep.action !== "TOGGLE_SELF_WITH_DEP") {
      return;
    }

    const pkgItem = rentItems.find((item) => item.Name === pkg.Name);
    const depItem = rentItems.find((item) => getPackageStateKey(item.Name) === dep.packageStateName);

    if (!pkgItem || !depItem) {
      return;
    }

    const pkgKey = getPackageStateKey(pkgItem.Name);
    const depKey = dep.packageStateName;

    if (pkgItem.Price === null) {
      nextState[pkgKey].isChecked = false;
    }
    if (depItem.Price === null) {
      nextState[depKey].isChecked = false;
    }

    if (pkgItem.Price !== null && depItem.Price !== null) {
      nextState[pkgKey].isChecked = !nextState[pkgKey].isChecked;
      nextState[depKey].isChecked = !nextState[depKey].isChecked;
    }
  });

  return nextState;
}

export function togglePackageStatus(
  pkgState: PackageState,
  pkgName: string,
  pkg: AdditionalServiceItem,
  options: {
    pageType: string;
    activeTab: number;
    rentItems: AdditionalServiceItem[];
    useNewCarRentLogic: boolean;
  }
): PackageState {
  if (options.useNewCarRentLogic) {
    if (SERVICE_PACKAGE_VALUES.includes(pkg.Value)) {
      return toggleNewCarRentServicePackage(pkgState, pkgName, pkg, options.rentItems);
    }

    if (pkg.Dependency?.length) {
      return toggleNewCarRentDependencyPackage(pkgState, pkg, options.rentItems);
    }

    if (!pkg.CantBeDeactivated) {
      return {
        ...pkgState,
        [pkgName]: {
          ...pkgState[pkgName],
          isChecked: !pkgState[pkgName].isChecked
        }
      };
    }

    return pkgState;
  }

  return {
    ...pkgState,
    [pkgName]: {
      ...pkgState[pkgName],
      isChecked: pkg.CantBeDeactivated ? pkgState[pkgName].isChecked : !pkgState[pkgName].isChecked
    }
  };
}

export function computeInstallmentWithPackages(
  baseInstallment: number | null | undefined,
  packageState: PackageState,
  activeItems?: AdditionalServiceItem[]
): number | null {
  if (baseInstallment == null) {
    return null;
  }

  const scopedState = activeItems ? filterPackageStateForItems(packageState, activeItems) : packageState;

  const packagesTotal = Object.values(scopedState).reduce((sum, entry) => {
    if (entry.isChecked && entry.Price != null) {
      return sum + Number(entry.Price);
    }
    return sum;
  }, 0);

  return baseInstallment + packagesTotal;
}

export function resolveAdditionalPackagesView(
  pageType: string,
  activeTab: number,
  isRentAvailable: boolean
): {
  itemsKey: "rent" | "leasing";
  stateKey: "rentForNewCar" | "default";
} {
  if (pageType === "new" && activeTab === 1 && isRentAvailable) {
    return { itemsKey: "rent", stateKey: "rentForNewCar" };
  }

  return {
    itemsKey: activeTab === 1 ? "rent" : "leasing",
    stateKey: "default"
  };
}

export function generateAdditionalPackageTiles(
  items: AdditionalServiceItem[],
  packageState: PackageState,
  onToggle: (pkgName: string, pkg: AdditionalServiceItem) => void
): React.ReactNode[] {
  return items.map((pkg) => (
    <AdditionalPackageTile
      package={pkg}
      key={`${pkg.Value}-${getPackageStateKey(pkg.Name)}`}
      packagesState={packageState}
      changePackageStatus={(pkgName: string) => onToggle(pkgName, pkg)}
      iconVisible
    />
  ));
}

export interface UseAdditionalPackagesParams {
  calculatorData: CalculatorData;
  pageType: string;
  activeTab: number;
  isRentAvailable: boolean;
  isElectric?: boolean;
  literals?: Record<string, string | undefined> | null;
}

export interface UseAdditionalPackagesResult {
  activePackageState: PackageState;
  packageTiles: React.ReactNode[];
  installmentWithPackages: number | null;
}

export function useAdditionalPackages({
  calculatorData,
  pageType,
  activeTab,
  isRentAvailable,
  isElectric = false,
  literals
}: UseAdditionalPackagesParams): UseAdditionalPackagesResult {
  const baseRentItems = useMemo(
    () =>
      buildAdditionalServiceItemsFromAdditions(
        calculatorData.RentAdditions,
        literals,
        isElectric,
        1,
        pageType,
        calculatorData.LeasingAdditions
      ),
    [calculatorData.RentAdditions, literals, isElectric, pageType]
  );

  const baseLeasingItems = useMemo(
    () =>
      buildAdditionalServiceItemsFromAdditions(
        calculatorData.LeasingAdditions,
        literals,
        isElectric,
        2,
        pageType
      ),
    [calculatorData.LeasingAdditions, literals, isElectric, pageType]
  );

  const selectedPrices = getSelectedPricesFromCalculatorData(calculatorData, activeTab);

  const rentItems = useMemo(
    () =>
      applyRentPackagePrices(
        baseRentItems,
        calculatorData,
        selectedPrices?.insurancePrice,
        calculatorData.InsuranceAdditionalText,
        pageType
      ),
    [baseRentItems, calculatorData, selectedPrices?.insurancePrice, pageType]
  );

  const leasingItems = useMemo(
    () =>
      applyLeasingPackagePrices(
        baseLeasingItems,
        calculatorData,
        selectedPrices?.insurancePrice,
        calculatorData.InsuranceAdditionalText
      ),
    [baseLeasingItems, calculatorData, selectedPrices?.insurancePrice]
  );

  const combinedItems = useMemo(
    () => mergeAdditionalServiceItems(rentItems, leasingItems),
    [rentItems, leasingItems]
  );

  const [packageState, setPackageState] = useState<PackageState>({});
  const [packageStateForNewCarRent, setPackageStateForNewCarRent] = useState<PackageState>({});

  useEffect(() => {
    setPackageState((prev) =>
      ensurePackageStateForItems(
        Object.keys(prev).length ? prev : createPackageStateFromItems(combinedItems),
        combinedItems
      )
    );
  }, [combinedItems]);

  useEffect(() => {
    setPackageStateForNewCarRent((prev) =>
      ensurePackageStateForItems(
        Object.keys(prev).length ? prev : createPackageStateFromItems(rentItems),
        rentItems
      )
    );
  }, [rentItems]);

  const view = resolveAdditionalPackagesView(pageType, activeTab, isRentAvailable);
  const activeItems = view.itemsKey === "rent" ? rentItems : leasingItems;
  const rawActivePackageState =
    view.stateKey === "rentForNewCar" ? packageStateForNewCarRent : packageState;
  const activePackageState = useMemo(
    () =>
      filterPackageStateForItems(
        ensurePackageStateForItems(rawActivePackageState, activeItems),
        activeItems
      ),
    [rawActivePackageState, activeItems]
  );

  const handlePackageToggle = useCallback(
    (pkgName: string, pkg: AdditionalServiceItem) => {
      const useNewCarRentLogic = pageType === "new" && activeTab === 1 && isRentAvailable;

      if (useNewCarRentLogic) {
        setPackageStateForNewCarRent((prev) =>
          togglePackageStatus(ensurePackageStateForItems(prev, rentItems), pkgName, pkg, {
            pageType,
            activeTab,
            rentItems,
            useNewCarRentLogic: true
          })
        );
        return;
      }

      setPackageState((prev) =>
        togglePackageStatus(ensurePackageStateForItems(prev, activeItems), pkgName, pkg, {
          pageType,
          activeTab,
          rentItems,
          useNewCarRentLogic: false
        })
      );
    },
    [pageType, activeTab, isRentAvailable, rentItems, activeItems]
  );

  const baseInstallment = selectedPrices?.installmentPrice ?? null;
  const installmentWithPackages = computeInstallmentWithPackages(baseInstallment, activePackageState);

  const packageTiles = useMemo(
    () => generateAdditionalPackageTiles(activeItems, activePackageState, handlePackageToggle),
    [activeItems, activePackageState, handlePackageToggle]
  );

  return {
    activePackageState,
    packageTiles,
    installmentWithPackages
  };
}
